import PDFDict from "../objects/PDFDict";
import PDFName from "../objects/PDFName";
import PDFArray from "../objects/PDFArray";
import PDFNumber from "../objects/PDFNumber";
import PDFString from "../objects/PDFString";
import PDFAnnotation from "./PDFAnnotation";
import { AnnotationFlags } from "./flags";
/**
 * Tipos de ação para links.
 */
export var LinkActionType;
(function (LinkActionType) {
    /** Link para URL externa */
    LinkActionType["URI"] = "URI";
    /** Link para destino interno (página) */
    LinkActionType["GoTo"] = "GoTo";
    /** Link para destino nomeado */
    LinkActionType["GoToR"] = "GoToR";
})(LinkActionType || (LinkActionType = {}));
/**
 * Representa uma anotação de link em um PDF.
 * Links podem apontar para URLs externas ou destinos internos do documento.
 */
class PDFLinkAnnotation extends PDFAnnotation {
    /**
     * Cria uma nova anotação de link.
     */
    static create(context) {
        const dict = context.obj({
            Type: 'Annot',
            Subtype: 'Link',
            Rect: [0, 0, 0, 0],
            Border: [0, 0, 0], // Sem borda por padrão
            F: AnnotationFlags.Print, // Imprime por padrão
        });
        return new PDFLinkAnnotation(dict);
    }
    /**
     * Cria uma anotação de link a partir de um dicionário existente.
     */
    static fromDict(dict) {
        return new PDFLinkAnnotation(dict);
    }
    constructor(dict) {
        super(dict);
    }
    /**
     * Define a URL de destino para o link (link externo).
     * @param uri A URL de destino
     */
    setURI(uri) {
        const action = this.dict.context.obj({
            S: 'URI',
            URI: PDFString.of(uri),
        });
        this.dict.set(PDFName.of('A'), action);
    }
    /**
     * Obtém a URL de destino do link.
     * @returns A URL ou undefined se não for um link de URL
     */
    getURI() {
        const action = this.dict.lookupMaybe(PDFName.of('A'), PDFDict);
        if (!action)
            return undefined;
        const type = action.lookup(PDFName.of('S'));
        if (!(type instanceof PDFName) || type.asString() !== '/URI') {
            return undefined;
        }
        const uri = action.lookup(PDFName.of('URI'));
        if (uri instanceof PDFString) {
            return uri.decodeText();
        }
        return undefined;
    }
    /**
     * Define um destino interno para o link (navega para uma página).
     * @param pageRef Referência da página de destino
     * @param x Posição X no destino (ou null para manter atual)
     * @param y Posição Y no destino (ou null para manter atual)
     * @param zoom Zoom no destino (ou null para manter atual)
     */
    setDestination(pageRef, x = null, y = null, zoom = null) {
        // Usa destino XYZ que permite especificar posição e zoom
        const dest = this.dict.context.obj([
            pageRef,
            'XYZ',
            x ?? null,
            y ?? null,
            zoom ?? null,
        ]);
        this.dict.set(PDFName.of('Dest'), dest);
    }
    /**
     * Define um destino nomeado para o link.
     * @param name Nome do destino
     */
    setNamedDestination(name) {
        this.dict.set(PDFName.of('Dest'), PDFString.of(name));
    }
    /**
     * Obtém o destino interno do link.
     */
    getDestination() {
        const dest = this.dict.get(PDFName.of('Dest'));
        if (dest instanceof PDFArray || dest instanceof PDFString) {
            return dest;
        }
        return undefined;
    }
    /**
     * Define a borda do link.
     * @param style Estilo da borda
     */
    setBorder(style) {
        const border = this.dict.context.obj([
            style.horizontalCornerRadius ?? 0,
            style.verticalCornerRadius ?? 0,
            style.width ?? 0,
        ]);
        if (style.dashArray && style.dashArray.length > 0) {
            const dashArrayObj = this.dict.context.obj(style.dashArray);
            border.push(dashArrayObj);
        }
        this.dict.set(PDFName.of('Border'), border);
    }
    /**
     * Remove a borda do link.
     */
    removeBorder() {
        this.dict.set(PDFName.of('Border'), this.dict.context.obj([0, 0, 0]));
    }
    /**
     * Define a cor da borda do link.
     * @param color Cor no formato [r, g, b] (0-1)
     */
    setBorderColor(color) {
        const colorComponents = this.extractColorComponents(color);
        const colorArray = this.dict.context.obj(colorComponents);
        this.dict.set(PDFName.of('C'), colorArray);
    }
    extractColorComponents(color) {
        // Color pode ser rgb(), grayscale(), cmyk()
        // Extrai os componentes numéricos
        const colorType = color.type;
        switch (colorType) {
            case 'RGB':
                return [
                    color.red,
                    color.green,
                    color.blue,
                ];
            case 'Grayscale':
                return [color.gray];
            case 'CMYK':
                return [
                    color.cyan,
                    color.magenta,
                    color.yellow,
                    color.key,
                ];
            default:
                // Tenta extrair se for objeto com propriedades de cor
                if ('red' in color && 'green' in color && 'blue' in color) {
                    return [
                        color.red,
                        color.green,
                        color.blue,
                    ];
                }
                return [0, 0, 1]; // Azul por padrão
        }
    }
    /**
     * Obtém a cor da borda.
     */
    getBorderColor() {
        const color = this.dict.lookupMaybe(PDFName.of('C'), PDFArray);
        if (!color)
            return undefined;
        const components = [];
        for (let i = 0; i < color.size(); i++) {
            const comp = color.lookup(i, PDFNumber);
            components.push(comp.asNumber());
        }
        return components;
    }
    /**
     * Define o modo de highlight quando o link é clicado.
     * @param mode N=nenhum, I=invertido, O=outline, P=push
     */
    setHighlightMode(mode) {
        this.dict.set(PDFName.of('H'), PDFName.of(mode));
    }
    /**
     * Configura o link para ser impresso ou não.
     */
    setPrint(print) {
        this.setFlagTo(AnnotationFlags.Print, print);
    }
}
export default PDFLinkAnnotation;
//# sourceMappingURL=PDFLinkAnnotation.js.map